import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import DownloadApp from "@/components/download-app";
import Faq from "@/components/faq";
import Header, { HeaderVariantEnum } from "@/components/header-v2";
import LoadingAnimation from "@/components/loading-animation";
import useAnalytics from "@/hooks/use-analytics";
import useCurrentLocation from "@/hooks/use-current-location";
import usePageLoading from "@/hooks/use-page-loading";
import usePWA from "@/hooks/use-pwa";
import type { RegionType } from "@/pages/cities";

import AllCities from "./all-cities";
import BlogPosts from "./blog-posts";
import NearbyCities from "./nearby-cities";
import Search from "./search";
import Stats from "./stats";
import type { DataType } from "./types";

export interface HomePageProps extends DataType {
  attractionCount: string;
  regions: RegionType[];
}

export default function HomePage({
  cities: { data: cities },
  homepage: {
    data: { attributes: homepage },
  },
  regions,
}: HomePageProps) {
  const { isPWA } = usePWA();
  const { toExploreUrl } = useCurrentLocation();
  const navigate = useNavigate();
  const pageLoading = usePageLoading();

  useEffect(() => {
    if (isPWA) {
      navigate({ replace: true, to: toExploreUrl() });
      return;
    }
  }, [isPWA, navigate, toExploreUrl]);

  const [headerVariant, setHeaderVariant] = useState<
    keyof typeof HeaderVariantEnum
  >(HeaderVariantEnum.simplified);

  const { page } = useAnalytics();

  useEffect(() => {
    page("home", "Home");
  }, [page]);

  const handleSearchFormVisibilityChange = (formIsHidden: boolean) =>
    setHeaderVariant(
      formIsHidden ? HeaderVariantEnum.standard : HeaderVariantEnum.simplified
    );

  return (
    <>
      <Header isHomepage strategy="fixed" variant={headerVariant} />
      {isPWA && pageLoading ? (
        <div className="flex h-screen">
          <LoadingAnimation />
        </div>
      ) : (
        <div className="relative z-1">
          <Search
            description={homepage.heroSubtitle}
            onSearchFormVisibilityChange={handleSearchFormVisibilityChange}
            title={homepage.heroTitle}
          />
          <NearbyCities cities={cities} />
          <Stats properties={homepage.ctaFactoids} title={homepage.ctaTitle} />
          <BlogPosts posts={homepage.blogLinks} />
          <AllCities regions={regions} />
          <section className="container pt-20 md:pt-41">
            <Faq items={homepage.faq} />
          </section>
          <div className="container">
            <DownloadApp
              className="pt-[247px] pb-20 md:pt-[356px] md:pb-[338px]"
              description={homepage.downloadCtaSubtitle}
              title={homepage.downloadCtaTitle}
            />
          </div>
        </div>
      )}
    </>
  );
}
